// Personal Drive (My Files) — cross-shell page (seller / supply / admin).
//
// Faz 1 (Volkan 2026-07-15 "bu yapı güzel"): the entity-bound Drive is kept
// intact and shown here as read-only "Operational" roots; a free personal/team
// space ("Personal") sits alongside with New Folder + Upload. One shared
// component, mounted by all three shells (DRY). MSW-backed simulation; Faz 2
// swaps in the additive backend behind the same endpoints. Storage pricing is
// parked — the capacity meter is a placeholder ("coming soon").

import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight,
  Cloud,
  FileText,
  Folder,
  FolderPlus,
  Home,
  ImageIcon,
  Lock,
  Package,
  Search,
  Star,
  Tag,
  Trash2,
  Upload,
  Users,
  Wrench,
} from 'lucide-react'
import {
  createDriveFolder,
  drivePersonalKeys,
  fetchDriveTree,
  fetchFolder,
  movePersonalFile,
  starPersonalFile,
  trashPersonalFile,
  uploadPersonalFile,
  type DriveTree,
  type FolderListing,
  type PersonalDriveFile,
  type PersonalDriveFolder,
} from '../../api/drive-personal'
import { useT } from '../../i18n'
import { drivePersonalI18n } from '../../i18n/shared/drive-personal'
import { drivePersonalExtraI18n } from '../../i18n/drive/personal-extra'
import { toast } from '../../lib/toast'
import { PersonalFilePreview } from './PersonalFilePreview'

type DriveT = (typeof drivePersonalI18n)['en']

// Custom drag payload type — distinguishes an internal file→folder move from an
// OS file drop (whose dataTransfer carries the 'Files' type instead).
const DND_FILE = 'application/x-drive-file-id'

// `dataTransfer.types` can be a DOMStringList in some browsers (no .includes),
// so copy to a real array before testing. An OS file drag reports the 'Files'
// type; an internal move reports our custom DND_FILE type.
function dragHasType(dt: DataTransfer, type: string): boolean {
  return Array.from(dt?.types ?? []).includes(type)
}
function dragHasFiles(dt: DataTransfer): boolean {
  return dragHasType(dt, 'Files')
}

const THUMB_HUES = ['#6366f1', '#0ea5e9', '#f43f5e', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#14b8a6']

function thumbGradient(seed: number): string {
  const a = THUMB_HUES[seed % THUMB_HUES.length]
  const b = THUMB_HUES[(seed * 3 + 2) % THUMB_HUES.length]
  return `linear-gradient(135deg, ${a}, ${b})`
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(0)} KB`
  const mb = kb / 1024
  if (mb < 1024) return `${mb.toFixed(1)} MB`
  return `${(mb / 1024).toFixed(2)} GB`
}

const OPERATIONAL_ICON: Record<string, typeof Package> = {
  'op-orders': Package,
  'op-products': Tag,
  'op-workorders': Wrench,
}

export function PersonalDrivePage() {
  const t = useT(drivePersonalI18n)
  const qc = useQueryClient()
  const [selectedId, setSelectedId] = useState('root')
  const [creating, setCreating] = useState(false)
  const [folderName, setFolderName] = useState('')
  const [search, setSearch] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const tree = useQuery({ queryKey: drivePersonalKeys.tree, queryFn: fetchDriveTree })
  const folder = useQuery({
    queryKey: drivePersonalKeys.folder(selectedId),
    queryFn: () => fetchFolder(selectedId),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: drivePersonalKeys.tree })
    qc.invalidateQueries({ queryKey: drivePersonalKeys.folder(selectedId) })
  }

  const createMut = useMutation({
    mutationFn: (name: string) =>
      createDriveFolder({ parent_id: selectedId === 'root' ? null : selectedId, name }),
    onSuccess: () => {
      setCreating(false)
      setFolderName('')
      invalidate()
    },
    onError: () => toast.error(t.loadError),
  })

  const uploadMut = useMutation({
    mutationFn: async (v: { folderId: string; files: File[] }) => {
      for (const f of v.files) {
        await uploadPersonalFile(v.folderId, f)
      }
    },
    onSuccess: (_data, v) => {
      toast.success(`${v.files.length} ${t.uploadFiles.toLowerCase()} ✓`)
      invalidate()
    },
    onError: () => toast.error(t.loadError),
  })

  const starMut = useMutation({
    mutationFn: (v: { id: string; starred: boolean }) =>
      starPersonalFile(v.id, v.starred),
    onSuccess: invalidate,
    onError: () => toast.error(t.loadError),
  })

  const trashMut = useMutation({
    mutationFn: (id: string) => trashPersonalFile(id),
    onSuccess: () => {
      toast.success('✓')
      invalidate()
    },
    onError: () => toast.error(t.loadError),
  })

  const moveMut = useMutation({
    mutationFn: (v: { fileId: string; folderId: string }) =>
      movePersonalFile(v.fileId, v.folderId),
    onSuccess: () => {
      toast.success(t.fileMoved)
      invalidate()
    },
    onError: () => toast.error(t.loadError),
  })

  const listing = folder.data
  const readonly = listing?.readonly ?? false
  const used = tree.data?.capacity.used_bytes ?? 0
  const [osDragging, setOsDragging] = useState(false)
  const [previewFile, setPreviewFile] = useState<PersonalDriveFile | null>(null)

  // The real folder an upload here should target. The backend/MSW resolves the
  // synthetic 'root' (My Files home) to the hidden home folder's id, so uploads
  // work at the root too; null on read-only views (Starred/Trash/operational).
  const uploadTarget = listing?.upload_folder_id ?? null
  const canUploadHere = uploadTarget != null

  const uploadTo = (folderId: string, files: File[]) => {
    if (files.length) uploadMut.mutate({ folderId, files })
  }

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length) {
      if (uploadTarget) uploadTo(uploadTarget, files)
      else toast.info(t.dropOnFolderHint)
    }
    e.target.value = ''
  }

  // OS file drag over the grid. `preventDefault` is called on EVERY file drag
  // (not gated on writability) so the browser never navigates to / opens the
  // dropped file. Detection is via a copied array so a DOMStringList works too.
  const onZoneDragOver = (e: React.DragEvent) => {
    if (!dragHasFiles(e.dataTransfer)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = canUploadHere ? 'copy' : 'none'
    if (canUploadHere) setOsDragging(true)
  }
  const onZoneDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOsDragging(false)
  }
  const onZoneDrop = (e: React.DragEvent) => {
    if (!dragHasFiles(e.dataTransfer)) return
    e.preventDefault()
    setOsDragging(false)
    const files = Array.from(e.dataTransfer.files ?? [])
    if (!files.length) return
    if (uploadTarget) uploadTo(uploadTarget, files)
    else toast.info(t.dropOnFolderHint)
  }
  const moveFileTo = (fileId: string, folderId: string) => {
    if (fileId) moveMut.mutate({ fileId, folderId })
  }

  return (
    <div className="w-full space-y-4">
      <DriveBanner title={t.pageTitle} subtitle={t.subtitle} used={formatBytes(used)}
        unlimited={t.capacityUnlimited} soon={t.capacitySoon} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[240px_1fr]">
        {/* Left: roots + actions */}
        <aside className="space-y-4">
          <div className="space-y-2">
            <button
              type="button"
              disabled={!canUploadHere || uploadMut.isPending}
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Upload className="h-4 w-4" /> {t.uploadFiles}
            </button>
            <input ref={fileInputRef} type="file" multiple hidden onChange={onPickFiles} />
            <button
              type="button"
              disabled={readonly}
              onClick={() => setCreating(true)}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-2 text-[13px] font-semibold text-[var(--color-text)] transition hover:border-indigo-400 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FolderPlus className="h-4 w-4" /> {t.newFolder}
            </button>
          </div>

          <DriveSidebar
            tree={tree.data}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onMoveToFolder={moveFileTo}
            rootLabel={t.pageTitle}
            personalLabel={t.sectionPersonal}
            operationalLabel={t.sectionOperational}
            autoTag={t.autoTag}
            starredLabel={t.starred}
            trashLabel={t.trash}
          />

          <CapacityMini used={formatBytes(used)} usedLabel={t.capacityUsed} soon={t.capacitySoon} />
        </aside>

        {/* Right: toolbar + grid */}
        <section className="flex min-h-[520px] flex-col overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-sm">
          <div className="flex flex-wrap items-center gap-3 border-b border-[var(--color-border)] px-4 py-3">
            <Breadcrumb listing={listing} homeLabel={t.home} onSelect={setSelectedId} />
            <div className="ml-auto flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-1.5 text-sm text-[var(--color-text-muted)]">
              <Search className="h-3.5 w-3.5" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.searchPlaceholder}
                aria-label={t.searchPlaceholder}
                className="w-40 bg-transparent text-[var(--color-text)] outline-none placeholder:text-[var(--color-text-muted)]"
              />
            </div>
          </div>

          {readonly && listing && <ReadonlyNote folderId={listing.folder.id} t={t} />}

          {creating && !readonly && (
            <NewFolderInline
              name={folderName}
              setName={setFolderName}
              onCancel={() => {
                setCreating(false)
                setFolderName('')
              }}
              onCreate={() => createMut.mutate(folderName)}
              pending={createMut.isPending}
              t={t}
            />
          )}

          <div
            className={`relative flex-1 overflow-x-auto p-4 transition-colors ${
              osDragging ? 'bg-indigo-50 ring-2 ring-inset ring-indigo-400' : ''
            }`}
            onDragOver={onZoneDragOver}
            onDragLeave={onZoneDragLeave}
            onDrop={onZoneDrop}
          >
            {osDragging && (
              <div className="pointer-events-none absolute inset-2 z-20 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-indigo-500 bg-indigo-100/90 text-indigo-700 shadow-inner">
                <Upload className="h-9 w-9 animate-bounce" />
                <p className="text-base font-bold">{t.dropToUpload}</p>
              </div>
            )}
            {folder.isLoading ? (
              <p className="p-8 text-sm text-[var(--color-text-muted)]">…</p>
            ) : folder.isError || !listing ? (
              <p className="p-8 text-sm text-[var(--color-text-muted)]">{t.loadError}</p>
            ) : (
              <FileGrid
                listing={listing}
                search={search}
                onOpenFolder={setSelectedId}
                onOpenFile={setPreviewFile}
                onStar={(id, starred) => starMut.mutate({ id, starred })}
                onTrash={(id) => trashMut.mutate(id)}
                onMove={moveFileTo}
                onUploadToFolder={uploadTo}
                t={t}
              />
            )}
          </div>
        </section>
      </div>

      <PersonalFilePreview file={previewFile} onClose={() => setPreviewFile(null)} />
    </div>
  )
}

// ─── Banner ──────────────────────────────────────────────────────────────────

function DriveBanner(props: {
  title: string
  subtitle: string
  used: string
  unlimited: string
  soon: string
}) {
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-4 rounded-xl px-6 py-5 text-white shadow"
      style={{
        background:
          'radial-gradient(120% 140% at 90% 0%, rgba(129,140,248,.5) 0%, rgba(79,70,229,0) 55%), linear-gradient(110deg, #4338ca 0%, #4f46e5 48%, #6366f1 100%)',
      }}
    >
      <div className="min-w-[240px]">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ring-1 ring-white/25">
          <Cloud className="h-3 w-3" /> PEXIFY Drive
        </span>
        <h1 className="mt-2 text-2xl font-bold tracking-tight">{props.title}</h1>
        <p className="mt-1 max-w-[46ch] text-[13px] text-white/80">{props.subtitle}</p>
      </div>
      <div className="text-right">
        <div className="flex items-baseline justify-end gap-1.5">
          <span className="text-2xl font-bold tabular-nums">{props.used}</span>
          <span className="text-[13px] text-white/75">{props.unlimited}</span>
        </div>
        <div className="my-2 h-1.5 w-44 overflow-hidden rounded-full bg-white/20">
          <div className="h-full w-1/3 rounded-full bg-white/85" />
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-white/40 bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold">
          ◷ {props.soon}
        </span>
      </div>
    </div>
  )
}

// ─── Sidebar (roots) ─────────────────────────────────────────────────────────

// Module-level (react-hooks/static-components): defined inside DriveSidebar it
// was recreated every render, remounting every nav button.
function NavBtn({
  id,
  label,
  icon: Icon,
  count,
  child,
  lock,
  dropTarget,
  selectedId,
  dropHover,
  setDropHover,
  onSelect,
  onMoveToFolder,
}: {
  id: string
  label: string
  icon: typeof Folder
  count?: number
  child?: boolean
  lock?: boolean
  dropTarget?: boolean
  selectedId: string
  dropHover: string | null
  setDropHover: React.Dispatch<React.SetStateAction<string | null>>
  onSelect: (id: string) => void
  onMoveToFolder: (fileId: string, folderId: string) => void
}) {
  const active = selectedId === id
  const over = dropTarget && dropHover === id
  const dropProps = dropTarget
    ? {
        onDragOver: (e: React.DragEvent) => {
          if (!dragHasType(e.dataTransfer, DND_FILE)) return
          e.preventDefault()
          e.dataTransfer.dropEffect = 'move'
          setDropHover(id)
        },
        onDragLeave: () => setDropHover((h) => (h === id ? null : h)),
        onDrop: (e: React.DragEvent) => {
          e.preventDefault()
          setDropHover(null)
          const fileId = e.dataTransfer.getData(DND_FILE)
          if (fileId) onMoveToFolder(fileId, id)
        },
      }
    : {}
  return (
    <button
      type="button"
      onClick={() => onSelect(id)}
      {...dropProps}
      className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13.5px] transition ${
        child ? 'pl-8' : ''
      } ${
        over
          ? 'bg-indigo-100 font-semibold text-indigo-700 ring-2 ring-indigo-400'
          : active
            ? 'bg-indigo-50 appearance-dark:bg-[var(--color-surface-muted)] font-semibold text-indigo-700 appearance-dark:text-[var(--color-info)] ring-1 ring-indigo-200 appearance-dark:ring-[var(--color-border)]'
            : 'font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]'
      }`}
    >
      <Icon className="h-4 w-4 shrink-0 opacity-90" />
      <span className="truncate">{label}</span>
      {lock ? (
        <Lock className="ml-auto h-3 w-3 text-amber-500" />
      ) : count != null ? (
        <span className="ml-auto text-[11px] tabular-nums text-[var(--color-text-muted)]">{count}</span>
      ) : null}
    </button>
  )
}

function DriveSidebar(props: {
  tree: DriveTree | undefined
  selectedId: string
  onSelect: (id: string) => void
  onMoveToFolder: (fileId: string, folderId: string) => void
  rootLabel: string
  personalLabel: string
  operationalLabel: string
  autoTag: string
  starredLabel: string
  trashLabel: string
}) {
  const [dropHover, setDropHover] = useState<string | null>(null)
  const { tree } = props
  if (!tree) return null
  const personal = tree.roots.find((r) => r.root_kind === 'personal')?.folders ?? []
  const operational = tree.roots.find((r) => r.root_kind === 'operational')?.folders ?? []

  return (
    <nav className="space-y-4">
      <div className="space-y-0.5">
        <div className="px-2 py-1 text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted)]">
          {props.personalLabel}
        </div>
        <NavBtn id="root" label={props.rootLabel} icon={Home} selectedId={props.selectedId} dropHover={dropHover} setDropHover={setDropHover} onSelect={props.onSelect} onMoveToFolder={props.onMoveToFolder} />
        {personal.map((f) => {
          if (f.system_view === 'starred') {
            return <NavBtn key={f.id} id={f.id} label={props.starredLabel} icon={Star} count={f.item_count} selectedId={props.selectedId} dropHover={dropHover} setDropHover={setDropHover} onSelect={props.onSelect} onMoveToFolder={props.onMoveToFolder} />
          }
          if (f.system_view === 'trash') {
            return <NavBtn key={f.id} id={f.id} label={props.trashLabel} icon={Trash2} selectedId={props.selectedId} dropHover={dropHover} setDropHover={setDropHover} onSelect={props.onSelect} onMoveToFolder={props.onMoveToFolder} />
          }
          return (
            <NavBtn key={f.id} id={f.id} label={f.name} icon={Folder} count={f.item_count} dropTarget selectedId={props.selectedId} dropHover={dropHover} setDropHover={setDropHover} onSelect={props.onSelect} onMoveToFolder={props.onMoveToFolder} />
          )
        })}
      </div>

      <div className="space-y-0.5">
        <div className="flex items-center gap-2 px-2 py-1 text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted)]">
          {props.operationalLabel}
          <span className="rounded-full bg-amber-100 px-1.5 py-px text-[9px] font-bold normal-case text-amber-700">
            {props.autoTag}
          </span>
        </div>
        {operational.map((f) => (
          <NavBtn key={f.id} id={f.id} label={f.name} icon={OPERATIONAL_ICON[f.id] ?? Folder} lock selectedId={props.selectedId} dropHover={dropHover} setDropHover={setDropHover} onSelect={props.onSelect} onMoveToFolder={props.onMoveToFolder} />
        ))}
      </div>
    </nav>
  )
}

// ─── Capacity mini ───────────────────────────────────────────────────────────

function CapacityMini(props: { used: string; usedLabel: string; soon: string }) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-sm">
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-bold tabular-nums text-[var(--color-text)]">{props.used}</span>
        <span className="text-[var(--color-text-muted)]">{props.usedLabel}</span>
      </div>
      <div className="my-2 h-1.5 overflow-hidden rounded-full bg-[var(--color-surface-muted)]">
        <div className="h-full w-1/3 rounded-full bg-indigo-500" />
      </div>
      <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-[var(--color-text-muted)]">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-300" /> {props.soon}
      </span>
    </div>
  )
}

// ─── Breadcrumb ──────────────────────────────────────────────────────────────

function Breadcrumb(props: {
  listing: FolderListing | undefined
  homeLabel: string
  onSelect: (id: string) => void
}) {
  const crumbs = props.listing?.breadcrumb ?? []
  return (
    <div className="flex min-w-0 items-center gap-1 text-sm">
      <Home className="h-4 w-4 text-[var(--color-text-muted)]" />
      {crumbs.map((c, i) => (
        <span key={c.id} className="flex min-w-0 items-center gap-1">
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-muted)]" />
          <button
            type="button"
            onClick={() => props.onSelect(c.id)}
            className={`truncate ${
              i === crumbs.length - 1
                ? 'font-semibold text-[var(--color-text)]'
                : 'text-[var(--color-text-muted)] hover:text-indigo-600'
            }`}
          >
            {c.name}
          </button>
        </span>
      ))}
    </div>
  )
}

// ─── Read-only note ──────────────────────────────────────────────────────────

function ReadonlyNote(props: { folderId: string; t: DriveT }) {
  const map: Record<string, string> = {
    'op-orders': props.t.readonlyOrders,
    'op-products': props.t.readonlyProducts,
    'op-workorders': props.t.readonlyWorkorders,
  }
  const text = map[props.folderId] ?? props.t.readonlyGeneric
  return (
    <div className="mx-4 mt-3 flex items-center gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-800">
      <Lock className="h-4 w-4 shrink-0" />
      <span>{text}</span>
    </div>
  )
}

// ─── New folder inline ───────────────────────────────────────────────────────

function NewFolderInline(props: {
  name: string
  setName: (v: string) => void
  onCancel: () => void
  onCreate: () => void
  pending: boolean
  t: DriveT
}) {
  return (
    <div className="mx-4 mt-3 flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2.5">
      <FolderPlus className="h-4 w-4 shrink-0 text-indigo-500" />
      <input
        autoFocus
        value={props.name}
        onChange={(e) => props.setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') props.onCreate()
          if (e.key === 'Escape') props.onCancel()
        }}
        placeholder={props.t.newFolderPlaceholder}
        aria-label={props.t.newFolderTitle}
        className="flex-1 rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-2.5 py-1.5 text-sm outline-none focus:border-indigo-400"
      />
      <button
        type="button"
        onClick={props.onCreate}
        disabled={props.pending}
        className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
      >
        {props.t.create}
      </button>
      <button
        type="button"
        onClick={props.onCancel}
        className="rounded-md px-2.5 py-1.5 text-sm font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
      >
        {props.t.cancel}
      </button>
    </div>
  )
}

// ─── File grid ───────────────────────────────────────────────────────────────

function FileGrid(props: {
  listing: FolderListing
  search: string
  onOpenFolder: (id: string) => void
  onOpenFile: (file: PersonalDriveFile) => void
  onStar: (id: string, starred: boolean) => void
  onTrash: (id: string) => void
  onMove: (fileId: string, folderId: string) => void
  onUploadToFolder: (folderId: string, files: File[]) => void
  t: DriveT
}) {
  const q = props.search.trim().toLowerCase()
  const subfolders = props.listing.subfolders.filter(
    (f) => !q || f.name.toLowerCase().includes(q),
  )
  const files = props.listing.files.filter((f) => !q || f.name.toLowerCase().includes(q))

  if (subfolders.length === 0 && files.length === 0) {
    const isTrash = props.listing.folder.system_view === 'trash'
    return (
      <div className="flex flex-col items-center gap-2 py-20 text-center text-[var(--color-text-muted)]">
        <Cloud className="h-12 w-12 text-[var(--color-text-muted)]" />
        <p className="text-sm font-medium">{isTrash ? props.t.emptyTrash : props.t.emptyFolder}</p>
        {!isTrash && !props.listing.readonly && (
          <p className="text-xs text-[var(--color-text-muted)]">{props.t.emptyFolderHint}</p>
        )}
      </div>
    )
  }

  return (
    <div
      className="grid gap-3.5"
      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(158px, 1fr))' }}
    >
      {subfolders.map((f) => (
        <FolderCard
          key={f.id}
          folder={f}
          onClick={() => props.onOpenFolder(f.id)}
          onDropFile={(fileId) => props.onMove(fileId, f.id)}
          onUploadFiles={(files) => props.onUploadToFolder(f.id, files)}
        />
      ))}
      {files.map((f) => (
        <FileCard
          key={f.id}
          file={f}
          sharedLabel={props.t.sharedWith}
          readonly={props.listing.readonly}
          onOpen={() => props.onOpenFile(f)}
          onStar={props.onStar}
          onTrash={props.onTrash}
        />
      ))}
    </div>
  )
}

function FolderCard(props: {
  folder: PersonalDriveFolder & { item_count: number }
  onClick: () => void
  onDropFile: (fileId: string) => void
  onUploadFiles: (files: File[]) => void
}) {
  const [over, setOver] = useState(false)
  // Only personal (writable) folders accept dropped files — both an internal
  // file move (DND_FILE) and an OS file drop (Files) upload INTO this folder.
  const droppable = props.folder.root_kind === 'personal' && !props.folder.readonly
  const accepts = (dt: DataTransfer) => dragHasType(dt, DND_FILE) || dragHasFiles(dt)
  return (
    <button
      type="button"
      onClick={props.onClick}
      onDragOver={
        droppable
          ? (e) => {
              if (!accepts(e.dataTransfer)) return
              e.preventDefault()
              // Stop the bubble to the grid zone — otherwise the zone's own
              // dragover runs afterwards and can reset dropEffect to 'none'
              // (e.g. at the My Files root), which shows a "can't drop" cursor
              // and suppresses the drop event on this folder.
              e.stopPropagation()
              e.dataTransfer.dropEffect = 'copy'
              setOver(true)
            }
          : undefined
      }
      onDragLeave={droppable ? () => setOver(false) : undefined}
      onDrop={
        droppable
          ? (e) => {
              if (!accepts(e.dataTransfer)) return
              e.preventDefault()
              e.stopPropagation() // don't let the zone also handle this drop
              setOver(false)
              const fileId = e.dataTransfer.getData(DND_FILE)
              if (fileId) {
                props.onDropFile(fileId)
                return
              }
              const files = Array.from(e.dataTransfer.files ?? [])
              if (files.length) props.onUploadFiles(files)
            }
          : undefined
      }
      className={`group flex flex-col overflow-hidden rounded-xl border bg-[var(--color-surface)] text-left transition hover:-translate-y-0.5 hover:shadow-md ${
        over ? 'border-indigo-500 ring-2 ring-indigo-400 bg-indigo-50' : 'border-[var(--color-border)] hover:border-[var(--color-border-strong)]'
      }`}
    >
      <div
        className="flex aspect-[4/3] items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)' }}
      >
        <Folder className={`h-9 w-9 ${over ? 'text-indigo-600' : 'text-indigo-400'}`} />
      </div>
      <div className="px-3 py-2.5">
        <p className="truncate text-[13px] font-semibold text-[var(--color-text)]">{props.folder.name}</p>
        <p className="text-[11px] tabular-nums text-[var(--color-text-muted)]">{props.folder.item_count}</p>
      </div>
    </button>
  )
}

function FileCard(props: {
  file: PersonalDriveFile
  sharedLabel: string
  readonly?: boolean
  onOpen?: () => void
  onStar?: (id: string, starred: boolean) => void
  onTrash?: (id: string) => void
}) {
  const { file } = props
  const tx = useT(drivePersonalExtraI18n)
  const isImage = file.kind === 'image'
  const canEdit = !props.readonly
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => props.onOpen?.()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          props.onOpen?.()
        }
      }}
      draggable={canEdit}
      onDragStart={
        canEdit
          ? (e) => {
              e.dataTransfer.setData(DND_FILE, file.id)
              e.dataTransfer.effectAllowed = 'move'
            }
          : undefined
      }
      className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] transition hover:-translate-y-0.5 hover:border-[var(--color-border-strong)] hover:shadow-md ${
        canEdit ? 'active:cursor-grabbing' : ''
      }`}
    >
      {canEdit && (
        <div className="absolute right-1.5 top-1.5 z-10 flex gap-1 opacity-0 transition group-hover:opacity-100">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              props.onStar?.(file.id, !file.starred)
            }}
            className="rounded-md bg-white/90 p-1 text-[var(--color-text-muted)] shadow-sm hover:text-amber-500"
            aria-label={tx.star}
          >
            <Star className={`h-3.5 w-3.5 ${file.starred ? 'fill-amber-400 text-amber-500' : ''}`} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              props.onTrash?.(file.id)
            }}
            className="rounded-md bg-white/90 p-1 text-[var(--color-text-muted)] shadow-sm hover:text-rose-500"
            aria-label={tx.trash}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <div
        className="relative flex aspect-[4/3] items-center justify-center"
        style={{ background: isImage ? thumbGradient(file.thumb_seed) : '#f1f5f9' }}
      >
        {isImage ? (
          <ImageIcon className="h-6 w-6 text-white/90 drop-shadow" />
        ) : (
          <FileText className="h-7 w-7 text-[var(--color-text-muted)]" />
        )}
        <span className="absolute bottom-1.5 left-1.5 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
          {file.kind}
        </span>
        {file.shared_count > 0 && (
          <span className="absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-teal-50 px-1.5 py-0.5 text-[10px] font-bold text-teal-700">
            <Users className="h-2.5 w-2.5" /> {file.shared_count}
          </span>
        )}
      </div>
      <div className="border-t border-[var(--color-border)] px-3 py-2.5">
        <p className="truncate text-[13px] font-semibold text-[var(--color-text)]" title={file.name}>
          {file.name}
        </p>
        <p className="text-[11px] tabular-nums text-[var(--color-text-muted)]">
          {formatBytes(file.size_bytes)} · {file.updated_at.slice(0, 10)}
        </p>
      </div>
    </div>
  )
}
